import React, { useMemo, useState } from "react";
import {
  Box,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { WIDGETS } from "./widgetRegistry";

export default function TileFrame({
  tile,
  switchOptions,
  onSwitch,
  layoutTypesInUse, // array of widget types currently in the dashboard
  children,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const options = useMemo(
    () => (switchOptions || []).filter((k) => !!WIDGETS[k]),
    [switchOptions],
  );

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handlePick = (newType) => {
    if (newType === tile.type) return;
    onSwitch(tile.id, newType);
    handleClose();
  };

  const isInUseElsewhere = (type) => {
    // If type appears in layout and is not the current tile's type, mark as "In use"
    if (!layoutTypesInUse?.length) return false;
    if (type === tile.type) return false;
    return layoutTypesInUse.includes(type);
  };

  return (
    <Box sx={{ position: "relative", height: "100%" }}>
      {/* Switch Icon Button */}
      {tile.switchable && options.length > 1 && (
        <Box sx={{ position: "absolute", top: 10, right: 10, zIndex: 2 }}>
          <IconButton
            size="small"
            onClick={handleOpen}
            sx={{
              background: "#fff",
              border: "1px solid #e6e6e6",
              "&:hover": { background: "#fff" },
            }}
          >
            <SwapHorizIcon fontSize="small" />
          </IconButton>

          {/* Menu */}
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            PaperProps={{
              sx: {
                width: 320,
                borderRadius: 2,
                boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                overflow: "hidden",
              },
            }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            <Box sx={{ px: 2, py: 1.25 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                Switch widget
              </Typography>
            </Box>
            <Divider />

            {options.map((key) => {
              const meta = WIDGETS[key];
              const Icon = meta?.icon;
              const isCurrent = key === tile.type;
              const inUse = isInUseElsewhere(key);
              const title = meta?.switchTitle || meta?.title || key;
              const subtitle = meta?.subtitle || "";

              return (
                <MenuItem
                  key={key}
                  disabled={isCurrent}
                  onClick={() => handlePick(key)}
                  sx={{
                    py: 1.2,
                    opacity: isCurrent ? 0.6 : 1,
                    alignItems: "flex-start",
                  }}
                >
                  {Icon ? (
                    <ListItemIcon sx={{ minWidth: 34, mt: 0.25 }}>
                      <Icon fontSize="small" />
                    </ListItemIcon>
                  ) : (
                    <ListItemIcon sx={{ minWidth: 34 }} />
                  )}

                  <ListItemText
                    sx={{ flex: 1, minWidth: 0, my: 0, pr: 1 }}
                    primary={
                      <Typography
                        noWrap
                        title={title}
                        sx={{
                          fontSize: 13,
                          fontWeight: 600,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {meta?.menuTitle || meta?.title || key}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        noWrap
                        sx={{
                          fontSize: 12,
                          color: "#718096",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {meta?.subtitle || ""}
                      </Typography>
                    }
                  />

                  {/* Right-side status chip */}
                  {isCurrent ? (
                    <Chip
                      label="Current"
                      size="small"
                      sx={{ fontSize: 11, height: 20, flexShrink: 0 }}
                    />
                  ) : inUse ? (
                    <Chip
                      label="In use"
                      size="small"
                      sx={{ fontSize: 11, height: 20, flexShrink: 0 }}
                    />
                  ) : null}
                </MenuItem>
              );
            })}
          </Menu>
        </Box>
      )}

      {children}
    </Box>
  );
}
